// A WEBSITE'S DESIGN SYSTEM IN AN AI ANSWER — the `design-system` view
// (functions/previewAnswer.js previewAiViews, functions/previewPagesAnswer.js
// previewDsLoad; the shell mounts it in the assistant's thread and on a Fly
// page with scope { params, embedded }). Read-only: the design-system
// showcase the preview's own sidebar embeds — colors, typography, spacing,
// icons, transitions, layout — over the site's own data, in the sandboxed
// user-code frame; a section switch remounts it over the frame's port. While
// reading: one frame-sized stand-in; no such site, no website at all, or a
// site with no design system: one sentence — a name that is no website says
// what it is, with its name (previewAnswerEmptyText).
export const PreviewDesignSystemView = {
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  height: '100%',
  minHeight: '0',
  dataPreviewDs: true,
  state: {
    site: null,
    failed: false,
    none: false,
    // The asked name when it is no website (functions/previewAnswer.js
    // previewAnswerResolve): a project of this workspace, or an App Store app.
    notWebsite: null,
    storeApp: null,
    section: null,
    current: null,
    ready: false,
    error: null
  },
  // EMPTY (the views contract, workspace functions/assistantAppViews.js): no
  // such site, or no website at all — the one sentence is all the view shows.
  empty: (el, s) => (s.failed ? true : s.site ? false : null),
  // RESOLVED PARAMS (the views contract): the site and environment the card
  // SHOWS — a follow-up stays on this site. null while reading.
  resolvedParams: (el, s) => (s.site ? { project: s.site.key, env: s.site.env } : null),
  onRender: (el) => el.call('previewDsLoad', el),
  // The site card's own inputs (SitePreviewView): the App Store list too —
  // a late list can change what the asked name means (canvasAnswerNamedMoved).
  subscribeTo: ['workspaceApps', '_projectsForActiveOrg', '_previewParams', '_wsAppsCatalog'],
  onUpdate: (el) => el.call('previewDsLoad', el),

  Skeleton: {
    width: '100%',
    aspectRatio: '16 / 10',
    round: 'radiusControl',
    ariaBusy: 'true',
    hide: (el, s) => !!s.site || !!s.failed
  },

  PreviewDsNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !s.failed,
    text: (el, s) => {
      void s.root.lang
      return el.call('previewAnswerEmptyText', s)
    }
  },

  PreviewDsHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !s.site,
    PreviewDsName: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        void s.root.lang
        const site = s.site
        if (!site) return ''
        return site.name + ' · ' + String(el.call('polyglot', el.call('previewEnvLabel', site.env)))
      }
    },
    // The section — the preview sidebar's design-system pages.
    InlineSelect: {
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.dsView.section')
      },
      value: (el, s) => s.current || 'colors',
      onClick: (e) => e.stopPropagation(),
      onChange: (ev, el, s) => {
        const next = ev.target.value
        if (next && next !== s.current) s.update({ section: next })
      },
      childProps: {
        tag: 'option',
        text: (el, s) => {
          void s.root.lang
          return el.call('polyglot', el.labelKey)
        },
        selected: (el, s) => (el.value === (s.current || 'colors') ? true : null)
      },
      children: () => [
        { key: 'ds-colors', value: 'colors', labelKey: 'preview.sidebar.ds.colors' },
        { key: 'ds-typography', value: 'typography', labelKey: 'preview.sidebar.ds.typography' },
        { key: 'ds-spacing', value: 'spacing', labelKey: 'preview.sidebar.ds.spacing' },
        { key: 'ds-icons', value: 'icons', labelKey: 'preview.sidebar.ds.icons' },
        {
          key: 'ds-transitions',
          value: 'transitions',
          labelKey: 'preview.sidebar.ds.transitions'
        },
        { key: 'ds-layout', value: 'layout', labelKey: 'preview.sidebar.ds.layout' }
      ]
    }
  },

  PreviewDsBox: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    flex: '1 1 auto',
    minHeight: 'F',
    round: 'radiusControl',
    background: 'overlay',
    hide: (el, s) => !s.site,
    PreviewDsFrame: {
      tag: 'iframe',
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      border: 'none',
      display: 'block',
      background: 'white',
      '@dark': { background: 'black' },
      title: (el, s) => {
        void s.root.lang
        return String(el.call('polyglot', 'preview.dsView.frameTitle')).replace('{name}', () =>
          s.site ? s.site.name : ''
        )
      },
      sandbox: (el) => el.call('userCodeSandbox')
    },
    // A sentence in place of the showcase while it mounts, or when it cannot.
    PreviewDsCover: {
      position: 'absolute',
      inset: '0',
      flow: 'y',
      align: 'center center',
      padding: 'A',
      background: 'overlay',
      hide: (el, s) => !!s.ready && !s.error,
      PreviewDsCoverText: {
        tag: 'p',
        role: 'status',
        margin: '0',
        maxWidth: 'G',
        fontSize: 'Z',
        color: 'caption',
        textAlign: 'center',
        text: (el, s) => {
          void s.root.lang
          const key = !s.error
            ? 'preview.answer.loadingPart'
            : s.error === 'empty'
              ? 'preview.dsView.empty'
              : 'preview.dsView.failed'
          return String(el.call('polyglot', key)).replace('{name}', () =>
            s.site ? s.site.name : ''
          )
        }
      }
    }
  }
}
